/**
 * PATH: packages/email-hub/src/feature/components/CostCategoryChip.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-COACHIP-001
 * PURPOSE: A small Chart-of-Accounts pill rendered on an open email when
 *          the backend email→job adjudicator classified it as a NON-job
 *          overhead/legal/supplier expense (e.g. a lawyer's retainer →
 *          "Professional Fees") rather than a customer job.
 * WHY:     Operator (Abhishek, 2026-06-07), on a DocuSign legal retainer
 *          that was wrongly stamped to a job + nagged "Add to CRM":
 *          *"what the fuck do you mean CRM this is absolutely under CoA
 *          lawyer"*. The CoA category was computed server-side but only
 *          surfaced on the kanban card; this puts the pill on the email
 *          itself so the operator sees the correct disposition inline.
 * FLOW:
 *   ┌───────────────────────────┐   ┌──────────────────┐   ┌──────────┐
 *   │ email.cost_category_label  │ → │ group → tint +   │ → │ render   │
 *   │ + cost_category_group      │   │ icon choice      │   │ pill     │
 *   └───────────────────────────┘   └──────────────────┘   └──────────┘
 * DEPENDENCIES:
 *   - lucide-react: Receipt (overhead) / Wrench (cogs) glyphs.
 *   - cn: class merge helper.
 * RELATED:
 *   - quotech-backend organisation/serializers/email/{listing,detail}.py
 *     (cost_category_label / cost_category_group — the read half).
 *   - SenderIdentityCard: the adjacent identity strip; this pill answers
 *     "what KIND of expense" where that answers "who is the sender".
 */
import { Receipt, Wrench } from 'lucide-react'

import { cn } from '../../_internal/lib/utils'
import type { Email } from '../hooks/useEmails.types'

interface CostCategoryChipProps {
  email: Pick<Email, 'cost_category_label' | 'cost_category_group'>
  className?: string
}

/** Whether the CoA pill should render at all. Pure: an email shows the
 *  pill only once the adjudicator has resolved a human label for it. */
export function shouldShowCostCategory(
  email: Pick<Email, 'cost_category_label'>,
): boolean {
  return Boolean((email.cost_category_label || '').trim())
}

export default function CostCategoryChip({
  email,
  className,
}: CostCategoryChipProps) {
  if (!shouldShowCostCategory(email)) return null

  const label = email.cost_category_label as string
  // Direct job cost (COGS) reads blue + a wrench (works); operating
  // overhead (rent, legal, insurance…) reads amber + a receipt. Default
  // to the overhead tint when the group is missing/unknown.
  const isCogs = (email.cost_category_group || '').toLowerCase() === 'cogs'
  const Icon = isCogs ? Wrench : Receipt

  return (
    <div
      data-testid="cost-category-chip"
      data-group={email.cost_category_group || 'overhead'}
      title={`Cost category - ${label}${isCogs ? ' (direct job cost)' : ' (overhead)'}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm',
        isCogs
          ? 'border-sky-200 dark:border-sky-900/50 bg-sky-50 dark:bg-sky-900/20 text-sky-800 dark:text-sky-200'
          : 'border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200',
        className,
      )}
    >
      <Icon
        className={cn(
          'h-4 w-4 shrink-0',
          isCogs
            ? 'text-sky-600 dark:text-sky-400'
            : 'text-amber-600 dark:text-amber-400',
        )}
        aria-hidden
      />
      <span className="font-medium">{label}</span>
    </div>
  )
}
